<template>
  <div>
    <v-container
      fluid
      :class="{
        'mx-12': !mobile,
        'my-6': !mobile,
        'px-4': mobile
      }"
    >
      <div
        :class="{
          'd-flex': true,
          'flex-row': !mobile,
          'flex-column': mobile,
          'mb-4': !mobile
        }"
      >
        <div
          :class="{
            'card-label': true,
            'd-flex': true,
            'flex-row': true,
            'mb-4': !mobile,
            'mb-2': mobile
          }"
        >
          <v-icon>mdi-checkbook</v-icon>
          <p class="mb-0 ml-4 subtitle-1 font-weight-bold">Post Dated Checks</p>
          <v-tooltip right>
            <template v-slot:activator="{ on }">
              <v-icon
                x-small
                class="pl-1"
                v-on="on"
                @click="redirectPDCDocumentation"
                >mdi-help-circle</v-icon
              >
            </template>
            <p class="white--text">Read the docs here!</p>
          </v-tooltip>
        </div>
        <div class="flex-grow-1"></div>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              :small="mobile"
              depressed
              rounded
              color="primary"
              :class="{
                'mr-2': !mobile,
                'mb-1': mobile,
                'mx-6': mobile,
                'font-weight-bold': true
              }"
              :disabled="!accessAllFeaturesGranted"
              @click="openProcessPDC"
            >
              <v-icon class="pr-1">mdi-format-list-checks</v-icon>Process Open
              PDCs
            </v-btn>
          </template>
          <p style="color: white">
            Easily process past and present due post dated checks
          </p>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              :small="mobile"
              :class="{
                'mr-2': !mobile,
                'mb-1': mobile,
                'mx-6': mobile,
                'font-weight-bold': true
              }"
              depressed
              rounded
              color="primary"
              @click="openGeneratePDCReportDialog"
              v-on="on"
            >
              <v-icon :small="mobile" :class="{ 'pr-1': !mobile }"
                >mdi-file-table</v-icon
              >Generate Report
            </v-btn>
          </template>
          <p style="color: white">
            Download the transactions as an Excel-compatible CSV file
          </p>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              :small="mobile"
              depressed
              rounded
              color="primary"
              :class="{
                'mr-2': !mobile,
                'mb-1': mobile,
                'mx-6': mobile,
                'font-weight-bold': true
              }"
              :disabled="!access.create"
              @click="openAddPDC"
              v-on="on"
            >
              <v-icon class="pr-1">mdi-plus</v-icon>Create Post Dated Check
            </v-btn>
          </template>
          <p style="color: white">Create a new Post Dated Check</p>
        </v-tooltip>
      </div>
      <v-row :class="{ 'mb-0': !mobile, 'mb-4': mobile }">
        <v-col class="py-0" cols="12" sm="9" md="9">
          <v-text-field
            v-model="searchString"
            solo
            flat
            append-icon="mdi-magnify"
            label="Search Tenant/Unit"
            hide-details
            :class="{
              'mb-6': !mobile,
              'mb-2': mobile
            }"
            :dense="mobile"
            @click:append="searchAction"
            @keydown.enter="searchAction"
          >
          </v-text-field>
        </v-col>
        <v-col class="py-0" cols="12" sm="3" md="3">
          <v-select
            v-model="selectedStatuses"
            solo
            flat
            hide-details
            label="Status"
            :items="statuses"
            multiple
            :dense="mobile"
            @change="searchAction"
          >
            <template v-slot:prepend-item>
              <v-list-item ripple @click="selectAllStatuses">
                <v-list-item-action>
                  <v-icon
                    :color="
                      selectedStatuses.length > 0 ? 'indigo darken-4' : ''
                    "
                    >{{ iconCategories }}</v-icon
                  >
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Select All</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="mt-2"></v-divider>
            </template>
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ selectedStatuses.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </v-col>
      </v-row>
      <PDCTable
        :search-string="searchString"
        :selected-statuses="selectedStatuses"
        :search-trigger="searchTrigger"
      ></PDCTable>
    </v-container>
    <GeneratePDCReport ref="generatePDCReport"></GeneratePDCReport>
    <ProcessPDC ref="processPDC"></ProcessPDC>
  </div>
</template>

<script>
import GeneratePDCReport from '../../components/pdc/GeneratePDCReport.vue'
import { PDCStatus, PDCStatusSelect } from '../../config/pdc'
import ProcessPDC from '../../components/pdc/ProcessPDC.vue'
import PDCTable from '../../components/tables/PDCTable.vue'

export default {
  components: {
    GeneratePDCReport,
    ProcessPDC,
    PDCTable
  },
  data() {
    return {
      searchString: '',
      selectedStatuses: [
        'open',
        'processed',
        'cleared',
        'canceled',
        'not_cleared',
        'pending_approval'
      ],
      statuses: PDCStatusSelect,
      pdcStatus: PDCStatus,
      searchTrigger: true
    }
  },
  computed: {
    access() {
      return this.$root.$roleAccess('pdc').access
    },
    accessAllFeaturesGranted() {
      return (
        this.access.read &&
        this.access.process &&
        this.access.clear &&
        this.access.not_clear
      )
    },
    iconCategories() {
      if (this.allStatusesSelected) return 'mdi-close-box'
      if (this.someStatusesSelected) return 'mdi-minus-box'
      return 'mdi-checkbox-blank-outline'
    },
    allStatusesSelected() {
      return this.selectedStatuses.length === this.statuses.length
    },
    someStatusesSelected() {
      return this.selectedStatuses.length > 0 && !this.allStatusesSelected
    },
    mobile() {
      return this.$vuetify.breakpoint.xs
    }
  },
  mounted() {
    // this.$store.dispatch('units/fetchUnitsFlatAction', {propertyId: this.property.propertyId})
    if (this.$store.state.pdcs.openProcess) {
      this.openProcessPDC()
    }
  },
  methods: {
    async reload() {
      this.searchTrigger = !this.searchTrigger
    },
    openAddPDC() {
      this.$router.push('/addMultiplePDC')
    },
    async searchAction() {
      await this.reload()
    },
    selectAllStatuses() {
      this.$nextTick(() => {
        if (this.allStatusesSelected) {
          this.selectedStatuses = []
        } else {
          this.selectedStatuses = this.statuses.slice().map((t) => t.value)
        }
        this.searchAction()
      })
    },

    redirectPDCDocumentation() {
      window.open('https://docs.spacepack.ph/post-dated-checks', '_blank')
    },
    openProcessPDC() {
      this.$refs.processPDC.open().then(async (result) => {
        if (result) {
          window.location.reload()
        }
      })
    },

    openGeneratePDCReportDialog() {
      this.$refs.generatePDCReport.open()
    }
  }
}
</script>

<style scoped>
.v-input {
  border-radius: 10px;
}
</style>
